import {Mapper} from '@s-ui/domain'

export class FromListVOToTagcloudListVOSeoMapper extends Mapper {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Object} deps.config
   * @param {Function} deps.tagcloudValueObjectFactory
   * */
  constructor({config, tagcloudListValueObjectFactory, tagcloudSeoValueObjectFactory, tagSeoValueObjectFactory}) {
    super()
    this._config = config
    this._culture = this._config.get('DEFAULT_CULTURE')
    this._tagcloudSeoValueObjectFactory = tagcloudSeoValueObjectFactory
    this._tagcloudListValueObjectFactory = tagcloudListValueObjectFactory
    this._tagSeoValueObjectFactory = tagSeoValueObjectFactory

    const {ADS_LIST_SUBSCRIPTION_PATH_PATTERN} = this._config.get('page')
    const {SEO_KEYWORDS, TAGCLOUD_LITERALS, SEMANTIC_URL_TYPES, SEO_KEYWORD_SLUG_TO_KEYWORD_MAPPING} =
      this._config.get('seo')
    const {VEHICLE_STATUS_RENTING} = this._config.get('advertising')
    const {TARGET_BUYER, TARGET_BUYER_IDS} = this._config.get('subscription')

    this._adsListSubscriptionPathPattern = ADS_LIST_SUBSCRIPTION_PATH_PATTERN
    this._seoKeywords = SEO_KEYWORDS
    this._seoKeywordSlugToKeywordMapping = SEO_KEYWORD_SLUG_TO_KEYWORD_MAPPING
    this._semanticUrlTypes = SEMANTIC_URL_TYPES
    this._tagcloudLiterals = TAGCLOUD_LITERALS
    this._vehicleStatusRenting = VEHICLE_STATUS_RENTING
    this._targetBuyer = TARGET_BUYER
    this._targetBuyerIds = TARGET_BUYER_IDS
  }

  setParams = ({bodyTypeId, fuelTypeId, makeId, canonical}) => {
    this._makeId = makeId
    this._bodyTypeId = bodyTypeId
    this._fuelTypeId = fuelTypeId
    this._canonical = canonical || ''
    return this
  }

  _getRentingKeywords() {
    return [
      this._seoKeywords.CHEAP,
      this._seoKeywords.NO_ENTRY_FEE,
      this._seoKeywords.NO_COMMITMENT,
      this._seoKeywords.BY_MONTHS
    ].map(keywordSlug =>
      this._getTagcloudRentingKeyword({
        keywordSlug
      })
    )
  }

  _getRentingCommitmentMonthsTagclouds(commitmentMonths) {
    const commitmentMonthsTagLabel = `${this._vehicleStatusRenting[this._culture]} ${
      this._tagcloudLiterals[this._culture].SUBSCRIPTION_KEYWORD_COMMITMENT_MONTHS_TAGCLOUD_TITLE
    }`.replace('%{commitmentMonths}', commitmentMonths)

    return {
      label: commitmentMonthsTagLabel,
      link: `${this._adsListSubscriptionPathPattern}${commitmentMonths}-meses/`,
      title: commitmentMonthsTagLabel
    }
  }

  _getTagcloudRentingKeyword({keywordSlug}) {
    if (!keywordSlug) return null
    const [rentingKeywordKey = null] =
      Object.entries(this._seoKeywords).find(([, value]) => value === keywordSlug) || []
    if (!rentingKeywordKey) return null

    const keywordTagLabel = `${this._vehicleStatusRenting[this._culture]} ${
      this._tagcloudLiterals[this._culture][`SUBSCRIPTION_KEYWORD_${rentingKeywordKey}_TAGCLOUD_TITLE`]
    }`

    return {
      label: keywordTagLabel,
      link: `${this._adsListSubscriptionPathPattern}${this._seoKeywords[rentingKeywordKey]}/`,
      title: keywordTagLabel
    }
  }

  _getTagcloudRentingBodyType({bodyTypes}) {
    if (!bodyTypes) return null

    return bodyTypes.map(({label, slug}) => {
      const bodyTypeLabel = `${this._vehicleStatusRenting[this._culture]} ${
        this._tagcloudLiterals[this._culture][`SUBSCRIPTION_${label}`]
      }`

      return {
        label: bodyTypeLabel,
        link: `${this._adsListSubscriptionPathPattern}${slug}/`,
        title: bodyTypeLabel
      }
    })
  }

  _getMakesTagcloud({makes}) {
    return makes.map(({name, slug}) => {
      const link = `${this._adsListSubscriptionPathPattern}${slug}/`
      const title = `${this._tagcloudLiterals[this._culture].SUBSCRIPTION_GENERIC_OFFER_TAG_TITLE} ${name}`
      return {label: name, link, title}
    })
  }

  _attachNoEntryFeeToMakesTags({makes}) {
    return makes.map(make => {
      const {label: name, link: slug} = make
      const link = `${slug}${this._seoKeywords.NO_ENTRY_FEE}/`
      const title = `${this._tagcloudLiterals[this._culture].SUBSCRIPTION_GENERIC_OFFER_TAG_TITLE} ${name} ${
        this._seoKeywordSlugToKeywordMapping[this._seoKeywords.NO_ENTRY_FEE]
      }`
      return {label: name, link, title}
    })
  }

  _getTargetBuyerTags() {
    return Object.values(this._targetBuyerIds).map(targetBuyerId => {
      const targetBuyerTagLabel = `${this._vehicleStatusRenting[this._culture]} ${
        this._tagcloudLiterals[this._culture][`SUBSCRIPTION_TARGET_BUYER_${targetBuyerId}_TAGCLOUD_TITLE`]
      }`

      return {
        label: targetBuyerTagLabel,
        link: `${this._adsListSubscriptionPathPattern}${this._targetBuyer[targetBuyerId]}/`,
        title: targetBuyerTagLabel
      }
    })
  }

  /**
   * @return {TagcloudValueObject}
   */
  map = ({makesListValueObject, modelsListValueObject}) => {
    const {FUEL_TYPES, VEHICLE_STATUS_RENTING} = this._config.get('advertising')
    const {specificFueltypes, bodyTypes, FUEL_TYPE_OTHER_ID} = this._config.get('search')
    const {
      FUEL_TYPE_ID_SLUG_MAPPING: fuelTypeIdSlugMapping,
      VEHICLE_STATE_ID_SLUG_MAPPING,
      VEHICLE_TYPE_SLUG_MAPPING
    } = this._config.get('page')
    const {VEHICLE_TYPES, VEHICLE_STATE_IDS} = this._config.get('subscription')

    // fuel types tag cloud
    const fuelTypeTags = specificFueltypes
      .map(({id, label}) => {
        const slug = fuelTypeIdSlugMapping[id]
        const link = `${this._adsListSubscriptionPathPattern}${slug}/`
        const title = `${this._tagcloudLiterals[this._culture].SUBSCRIPTION_CAR_OFFER_TAG_TITLE} ${
          FUEL_TYPES[id][this._culture]
        }`
        return id !== FUEL_TYPE_OTHER_ID ? {label: FUEL_TYPES[id][this._culture], link, title} : null
      })
      .filter(Boolean)

    const bodyTypeTagcloudTitle = this._tagcloudLiterals[this._culture].SUBSCRIPTION_BODY_TYPES_TAGCLOUD_TITLE
    const bodyTypeTags = this._getTagcloudRentingBodyType({bodyTypes})

    const bodyTypeTagcloud = this._tagcloudSeoValueObjectFactory({
      tags: bodyTypeTags,
      title: bodyTypeTagcloudTitle
    })

    const fuelTypeTagcloudTitle = this._tagcloudLiterals[this._culture].SUBSCRIPTION_FUEL_TYPES_TAGCLOUD_TITLE

    const fuelTypeTagcloud = this._tagcloudSeoValueObjectFactory({
      tags: fuelTypeTags,
      title: fuelTypeTagcloudTitle
    })

    const hasFuelTypeId = Boolean(this._fuelTypeId)
    const selectedFuelTypeSlug = hasFuelTypeId ? fuelTypeIdSlugMapping[this._fuelTypeId] : ''
    this._fuelTypeName = hasFuelTypeId ? FUEL_TYPES[this._fuelTypeId][this._culture] : ''

    // makes with fuel type tag cloud
    const {items: makesItems} = makesListValueObject.toJSON()
    const makesWithFueltypeTags = hasFuelTypeId
      ? makesItems.map(({name, slug}) => {
          const link = `${this._adsListSubscriptionPathPattern}${slug}/${selectedFuelTypeSlug}/`
          const title = `${this._tagcloudLiterals[this._culture].SUBSCRIPTION_GENERIC_OFFER_TAG_TITLE} ${name} ${
            FUEL_TYPES[this._fuelTypeId][this._culture]
          }`
          return {label: name, link, title}
        })
      : []
    const makesWithFueltypeTagcloudTitle = this._tagcloudLiterals[
      this._culture
    ].SUBSCRIPTION_FUEL_TYPES_MAKES_TAGCLOUD_TITLE.replace('%{fuelTypeName}', this._fuelTypeName.toLowerCase())
    const makesWithFueltypeTagcloud = this._tagcloudSeoValueObjectFactory({
      tags: makesWithFueltypeTags,
      title: makesWithFueltypeTagcloudTitle
    })

    // makes tag cloud
    let makesTags = this._getMakesTagcloud({makes: makesItems})
    const makesTagcloudTitle = this._tagcloudLiterals[this._culture].SUBSCRIPTION_MAKES_TAGCLOUD_TITLE
    // If canonical includes sin-entrada, append no entry fee to makes tags
    const semanticKeys = this._canonical?.split('/renting')?.[1]?.split('/').filter(Boolean) || []
    const isIncludeNoEntryFeeInMakes = semanticKeys.includes(this._seoKeywords.NO_ENTRY_FEE)
    if (isIncludeNoEntryFeeInMakes) {
      makesTags = this._attachNoEntryFeeToMakesTags({makes: makesTags})
    }
    const makesTagcloud = this._tagcloudSeoValueObjectFactory({
      tags: makesTags,
      title: makesTagcloudTitle
    })

    const currentMake = makesItems.find(({id}) => String(this._makeId) === id)
    this._makeSlug = currentMake?.slug
    this._makeName = currentMake?.name

    // models tag cloud
    const hasMakeId = this._makeId
    const models = modelsListValueObject.getItems()
    const currentMakeModelsTags = models
      .map(subscriptionModel => {
        const make = subscriptionModel.getMake()
        const model = subscriptionModel.getModel()
        if (String(this._makeId) === make.getId()) {
          const {name, slug} = model.toJSON()

          const link = `${this._adsListSubscriptionPathPattern}${this._makeSlug}/${slug.replace(/-/g, '_')}/`
          const title = `${this._tagcloudLiterals[this._culture].SUBSCRIPTION_GENERIC_OFFER_TAG_TITLE} ${
            this._makeName
          } ${name}`
          return {label: name, link, title}
        }
        return null
      })
      .filter(Boolean)

    const modelsTagcloud = this._tagcloudSeoValueObjectFactory({
      tags: currentMakeModelsTags,
      title: this._tagcloudLiterals[this._culture].SUBSCRIPTION_MAKE_MODELS_TAGCLOUD_TITLE.replace(
        '%{makeName}',
        this._makeName
      )
    })

    // vehicle states tag cloud
    const vehicleStateNewTagLabel = `${VEHICLE_STATUS_RENTING[this._culture]} ${
      this._tagcloudLiterals[this._culture].SUBSCRIPTION_VEHICLE_STATE_NEW_TAGCLOUD_TITLE
    }`
    const vehicleStateNewTag = {
      label: vehicleStateNewTagLabel,
      link: `${this._adsListSubscriptionPathPattern}${VEHICLE_STATE_ID_SLUG_MAPPING[VEHICLE_STATE_IDS.NEW]}/`,
      title: vehicleStateNewTagLabel
    }
    const vehicleStateUsedTagLabel = `${VEHICLE_STATUS_RENTING[this._culture]} ${
      this._tagcloudLiterals[this._culture].SUBSCRIPTION_VEHICLE_STATE_USED_TAGCLOUD_TITLE
    }`
    const vehicleStateUsedTag = {
      label: vehicleStateUsedTagLabel,
      link: `${this._adsListSubscriptionPathPattern}${VEHICLE_STATE_ID_SLUG_MAPPING[VEHICLE_STATE_IDS.USED]}/`,
      title: vehicleStateUsedTagLabel
    }

    const targetBuyerTags = this._getTargetBuyerTags()

    const rentingKeywordsTags = this._getRentingKeywords()
    const rentingCommitmentMonthsTags = [12, 24, 36, 48].map(this._getRentingCommitmentMonthsTagclouds.bind(this))

    const vehicleTypeIndustrialTagLabel = `${VEHICLE_STATUS_RENTING[this._culture]} ${
      this._tagcloudLiterals[this._culture].SUBSCRIPTION_VEHICLE_TYPE_INDUSTRIALS_TAGCLOUD_TITLE
    }`
    const vehicleTypeIndustrialTag = {
      label: vehicleTypeIndustrialTagLabel,
      link: `${this._adsListSubscriptionPathPattern}${VEHICLE_TYPE_SLUG_MAPPING[VEHICLE_TYPES.INDUSTRIAL]}/`,
      title: vehicleTypeIndustrialTagLabel
    }

    const vehicleStateTags = this._tagcloudSeoValueObjectFactory({
      tags: [
        vehicleStateUsedTag,
        vehicleStateNewTag,
        ...targetBuyerTags,
        ...rentingKeywordsTags,
        ...rentingCommitmentMonthsTags,
        vehicleTypeIndustrialTag
      ],
      title: this._tagcloudLiterals[this._culture].SUBSCRIPTION_OTHER_LINKS
    })

    return this._tagcloudListValueObjectFactory({
      tagclouds: [
        bodyTypeTagcloud,
        fuelTypeTagcloud,
        hasFuelTypeId ? makesWithFueltypeTagcloud : false,
        makesTagcloud,
        hasMakeId ? modelsTagcloud : false,
        vehicleStateTags
      ].filter(Boolean)
    })
  }
}
